import React from 'react';
import Image from 'next/image';
import { useTranslations } from '../../locales/useTranslations';
import FadeInAnimation from '../Common/FadeInAnimation';

const QR_FLOW_URL = 'https://qr-flow.eu';

const SMART_RULE_KEYS = ['scan_limit', 'schedule', 'recurring', 'language', 'context', 'geo', 'geo_fine', 'device', 'ab', 'passthrough'];

const QrFlowPlatform = () => {
    const { t } = useTranslations();

    return (
        <>
            <section className="qr-flow-platform" id="campaign-dashboard">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-6 order-lg-2">
                            <FadeInAnimation direction="left" delay={80}>
                                <figure className="qr-flow-platform__figure">
                                    <Image
                                        src="/images/qr-flow/campaign-dashboard.webp"
                                        alt={t('qrFlow_campaign_imageAlt')}
                                        width={1440}
                                        height={900}
                                        className="qr-flow-platform__image"
                                        sizes="(max-width: 992px) 100vw, 50vw"
                                    />
                                    <figcaption>{t('qrFlow_campaign_caption')}</figcaption>
                                </figure>
                            </FadeInAnimation>
                        </div>
                        <div className="col-lg-6 order-lg-1">
                            <FadeInAnimation direction="right" delay={50}>
                                <span className="qr-flow-platform__badge">{t('qrFlow_campaign_badge')}</span>
                                <h2 className="qr-flow-platform__title">{t('qrFlow_campaign_title')}</h2>
                                <p className="qr-flow-platform__text">{t('qrFlow_campaign_p1')}</p>
                                <p className="qr-flow-platform__text">{t('qrFlow_campaign_p2')}</p>
                                <ul className="qr-flow-platform__list">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <li key={n}>{t(`qrFlow_campaign_highlight${n}`)}</li>
                                    ))}
                                </ul>
                                <a
                                    href={`${QR_FLOW_URL}/register/`}
                                    className="qr-flow-platform__link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('qrFlow_campaign_cta')}
                                </a>
                            </FadeInAnimation>
                        </div>
                    </div>
                </div>
            </section>

            <section className="qr-flow-smart-redirects" id="smart-redirects">
                <div className="container">
                    <FadeInAnimation direction="up" delay={50}>
                        <div className="qr-flow-smart-redirects__intro">
                            <span className="qr-flow-smart-redirects__badge">{t('qrFlow_smart_badge')}</span>
                            <h2>{t('qrFlow_smart_title')}</h2>
                            <p>{t('qrFlow_smart_subtitle')}</p>
                        </div>
                    </FadeInAnimation>

                    <div className="row g-5 align-items-start">
                        <div className="col-lg-6">
                            <FadeInAnimation direction="right" delay={80}>
                                <figure className="qr-flow-platform__figure">
                                    <Image
                                        src="/images/qr-flow/smart-redirects.webp"
                                        alt={t('qrFlow_smart_imageAlt')}
                                        width={1440}
                                        height={900}
                                        className="qr-flow-platform__image"
                                        sizes="(max-width: 992px) 100vw, 50vw"
                                    />
                                    <figcaption>{t('qrFlow_smart_caption')}</figcaption>
                                </figure>
                            </FadeInAnimation>
                        </div>
                        <div className="col-lg-6">
                            <FadeInAnimation direction="left" delay={100}>
                                <p className="qr-flow-smart-redirects__text">{t('qrFlow_smart_p1')}</p>
                                <p className="qr-flow-smart-redirects__text">{t('qrFlow_smart_p2')}</p>
                                <div className="qr-flow-smart-redirects__pipeline">
                                    <h3>{t('qrFlow_smart_pipeline_title')}</h3>
                                    <p>{t('qrFlow_smart_pipeline_desc')}</p>
                                    <ol>
                                        {[1, 2, 3, 4].map((n) => (
                                            <li key={n}>
                                                <strong>{t(`qrFlow_smart_pipeline_step${n}_title`)}</strong>
                                                <span>{t(`qrFlow_smart_pipeline_step${n}_desc`)}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </FadeInAnimation>
                        </div>
                    </div>

                    <div className="qr-flow-smart-redirects__rules">
                        {SMART_RULE_KEYS.map((key, index) => (
                            <FadeInAnimation key={key} direction="up" delay={60 + index * 25}>
                                <article className="qr-flow-smart-redirects__rule">
                                    <h4>{t(`qrFlow_smart_rule_${key}_title`)}</h4>
                                    <p>{t(`qrFlow_smart_rule_${key}_desc`)}</p>
                                </article>
                            </FadeInAnimation>
                        ))}
                    </div>

                    <FadeInAnimation direction="up" delay={120}>
                        <p className="qr-flow-smart-redirects__footer">
                            {t('qrFlow_smart_footer_prefix')}
                            <a href={`${QR_FLOW_URL}/#smart-redirects`} target="_blank" rel="noopener noreferrer">
                                qr-flow.eu
                            </a>
                            {t('qrFlow_smart_footer_suffix')}
                        </p>
                    </FadeInAnimation>
                </div>
            </section>
        </>
    );
};

export default QrFlowPlatform;
